import React, { useEffect, useState } from 'react';
import AppointmentsTable from './AppointmentsTable';
import AppointmentsCalendar from './AppointmentsCalendar';
import AppointmentsGantt from './AppointmentsGantt';
import AppointmentDetailsModal from './AppointmentDetailsModal';
import DateRangePicker from '../ui/DateRangePicker';
import { getDealerAppointments } from '../../services/appointment';
import styled from '@emotion/styled';
import { format, startOfYear, endOfYear } from 'date-fns';
import DotsLoading from '../ui/DotsLoading';
import { Table, Calendar, BarChart3 } from 'lucide-react';

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: scroll;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 16px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Label = styled.div`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const ViewToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    margin-left: 0;
    gap: 4px;
  }
`;

const ViewToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.active ? '#F43F60' : '#353556'};
  background: ${props => props.active ? '#F43F60' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#9E9E9E'};
  border-radius: 8px;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #353556;
    border: 1px solid #F43F60;
    color: #fff;
    background: #F43F60;
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 0.8rem;
    gap: 4px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 0.75rem;
    gap: 3px;
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #F43F60;
  text-align: center;
  margin: 40px 0;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    margin: 32px 0;
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    margin: 24px 0;
    font-size: 0.875rem;
  }
`;

const AppointmentsTableContainer = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState([startOfYear(new Date()), endOfYear(new Date())]);
    const [viewType, setViewType] = useState('table'); // 'table', 'calendar', 'gantt'
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Transform API response to match frontend expectations
    const transformAppointments = (apiAppointments) => {
        return apiAppointments.map(appt => ({
            id: appt.id,
            dealerId: appt.dealerId,
            offerId: appt.offerId,
            date: appt.date,
            status: appt.status,
            notes: appt.notes,
            created_at: appt.created_at,
            updated_at: appt.updated_at,
            // Transform time slot
            timeSlot: {
                start: appt.timeSlotStart,
                end: appt.timeSlotEnd
            },
            // Transform offer data
            offer: {
                amount: appt.offer_amount,
                status: appt.offer_status,
                // Transform user data
                user: {
                    userInfo: {
                        name: appt.user_name,
                        email: appt.user_email,
                        phone: null // API doesn't provide phone
                    }
                },
                // Transform vehicle data
                vehicle: {
                    make: appt.make,
                    model: appt.model,
                    year: appt.year,
                    price: appt.price,
                    stockId: appt.stock_no,
                    vin: appt.vin,
                    isUsed: appt.new_used === 'Used',
                    images: [] // API doesn't provide images
                }
            }
        }));
    };

    useEffect(() => {
        const fetchAppointments = async () => {
            setLoading(true);
            setError('');
            try {
                const res = await getDealerAppointments({
                    startDate: format(dateRange[0], 'yyyy-MM-dd'),
                    endDate: format(dateRange[1], 'yyyy-MM-dd'),
                });
                if (res.status === 'success') {
                    const transformedAppointments = transformAppointments(res.data.appointments || []);
                    setAppointments(transformedAppointments);
                } else {
                    setError(res.message || 'Failed to fetch appointments');
                }
            } catch (err) {
                setError('Failed to fetch appointments');
            } finally {
                setLoading(false);
            }
        };
        fetchAppointments();
    }, [dateRange]);

    const handleAppointmentClick = (appointment) => {
        setSelectedAppointment(appointment);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedAppointment(null);
    };

    const renderView = () => {
        if (loading) return <DotsLoading />;
        if (error) return <ErrorMessage>{error}</ErrorMessage>;

        switch (viewType) {
            case 'calendar':
                return <AppointmentsCalendar appointments={appointments} onAppointmentClick={handleAppointmentClick} />;
            case 'gantt':
                return <AppointmentsGantt appointments={appointments} onAppointmentClick={handleAppointmentClick} />;
            default:
                return <AppointmentsTable appointments={appointments} onAppointmentClick={handleAppointmentClick} />;
        }
    };

    return (
        <Container>
            <FilterBar>
                <Label>Date Range:</Label>
                <DateRangePicker
                    value={dateRange}
                    onChange={setDateRange}
                />
                <ViewToggleContainer>
                    <ViewToggleButton
                        active={viewType === 'table'}
                        onClick={() => setViewType('table')}
                        title="Table View"
                    >
                        <Table />
                        <span>Table</span>
                    </ViewToggleButton>
                    <ViewToggleButton
                        active={viewType === 'calendar'}
                        onClick={() => setViewType('calendar')}
                        title="Calendar View"
                    >
                        <Calendar />
                        <span>Calendar</span>
                    </ViewToggleButton>
                    <ViewToggleButton
                        active={viewType === 'gantt'}
                        onClick={() => setViewType('gantt')}
                        title="Gantt View"
                    >
                        <BarChart3 />
                        <span>Gantt</span>
                    </ViewToggleButton>
                </ViewToggleContainer>
            </FilterBar>
            {renderView()}
            
            {/* Appointment Details Modal */}
            <AppointmentDetailsModal
                appointment={selectedAppointment}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
        </Container>
    );
};

export default AppointmentsTableContainer; 